/**
 * @file: MessageBubble.tsx
 *
 * A single chat message row (pure). Renders, in order: an optional
 * time-slice marker, system notifications as centered text, and otherwise
 * a left/right bubble with avatar, optional quoted-message snapshot,
 * `@mention`-highlighted text or a rich payload, and — for own messages —
 * a send-status / read-receipt column.
 *
 * Rendered inside an inverted FlatList, so the time marker is emitted LAST
 * (the list flips it ABOVE the row).
 *
 * Long-press opens the action menu: the bubble measures its own on-screen rect
 * (`measureInWindow` — window coords, unaffected by the list's `scaleY(-1)`
 * flip) and hands it, plus a clone of its body, to `onPresentMenu`; the
 * app-root overlay host (`WiredBubbleMenu`) lifts the clone + shows the menu.
 */

import { Typography } from '@euler/app/components/typography';
import { BotAvatar } from '@euler/app/flows/chat/components/BotAvatar';
import { Avatar } from '@euler/components/Avatar';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatMessagePayload, ChatMsg } from '@euler/model/chat';
import { IdType } from '@euler/model/common';
import * as Haptics from 'expo-haptics';
import { MotiView } from 'moti';
import {
  FC,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  LayoutChangeEvent,
  Platform,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { runOnJS } from 'react-native-reanimated';
import {
  BUBBLE_PRESS_SCALE,
  BubbleMenuTarget,
} from '../../lib/conversation/hooks/useBubbleMenu';
import { BubbleQuoteSnapshot } from './BubbleQuoteSnapshot';
import { BubbleReactions } from './BubbleReactions';
import { MentionedText } from './MentionedText';
import { MessagePayloadView, isRichPayloadType } from './MessagePayloadView';
import { MessageTimeMarker } from './MessageTimeMarker';
import { OwnMessageStatus, OwnMessageStatusKind } from './OwnMessageStatus';
import { SystemMessageText } from './SystemMessageText';

const RECEIVED_BUBBLE_BG = '#eeeef0';
/** Render a time-slice marker when the gap to the prev message exceeds this. */
const TIME_SLICE_GAP_MS = 5 * 60 * 1000;

// Single source of truth for the bubble's wrap geometry — used by BOTH the
// styles below AND the lifted-clone wrap-width computation, so they can't
// drift if the layout is tweaked.
/** Bubble column width as a fraction of the row's content box. */
export const BUBBLE_COLUMN_FRACTION = 0.8;
const COLUMN_MAX_WIDTH = `${BUBBLE_COLUMN_FRACTION * 100}%` as `${number}%`;
/** The row's horizontal padding, in `rpx` units (applied each side). */
const ROW_PADDING_RPX = 2;

/** A confirmed, non-deleted message gets the long-press action menu (when a
 * present handler is wired). Single predicate so the open-gate + the long-press
 * trigger can't drift. */
const isMenuable = (message: ChatMsg, hasMenu: boolean): boolean =>
  hasMenu && message.kind === 'remote' && !message.deletedAt;

export const MessageBubble: FC<{
  message: ChatMsg;
  isMine: boolean;
  myPlatformUserId: IdType;
  roomType: 'private' | 'group';
  prevMessageCreatedAt: string | null;
  highlighted?: boolean;
  style?: StyleProp<ViewStyle>;
  /** Bubble column max width in px, measured ONCE at the list level (the row
   * width is identical for every bubble). Falls back to `80%` if absent. */
  maxBubbleWidth?: number;
  /** Render the time marker BEFORE the bubble instead of after. The marker
   * must appear visually ABOVE its message; whether that means first or last
   * depends on how the list backend's inverted cell handles child order
   * (FlatList reverses it, FlashList doesn't), so the backend decides. */
  timeMarkerFirst?: boolean;
  // Long-press → present the action menu. When wired, a confirmed,
  // non-deleted message measures itself + presents the menu.
  onPresentMenu?: (target: BubbleMenuTarget) => void;
  /** Toggle a reaction emoji on this message (tapping a reaction chip). */
  onReact?: (message: ChatMsg, emoji: string) => void;
  onQuotePress?: (message: ChatMsg) => void;
  onPreviewImage?: (url: string) => void;
  onPreviewVideo?: (url: string) => void;
  onOpenUrl?: (url: string) => void;
  onOpenCard?: (payload: ChatMessagePayload) => void;
  onRetry?: (message: ChatMsg) => void;
  onShowReaders?: (message: ChatMsg) => void;
  onAudioPlay?: (message: ChatMsg) => void;
  /** Tapping a bot reply's suggested-question chip. */
  onSuggestionPress?: (question: string) => void;
}> = memo(
  ({
    message,
    isMine,
    myPlatformUserId,
    roomType,
    prevMessageCreatedAt,
    highlighted,
    style,
    maxBubbleWidth,
    timeMarkerFirst,
    onPresentMenu,
    onReact,
    onQuotePress,
    onPreviewImage,
    onPreviewVideo,
    onOpenUrl,
    onOpenCard,
    onRetry,
    onShowReaders,
    onAudioPlay,
    onSuggestionPress,
  }) => {
    const { rpx } = useResponsiveContext();
    const isBotMessage = message.authorType === 'bot';
    // The measured bubble column + its current rendered body, read at
    // long-press time to present the lifted clone + menu.
    const bubbleRef = useRef<View>(null);
    const cloneRef = useRef<ReactNode>(null);
    // The bubble's laid-out (unscaled, rendered) size, captured on layout.
    // Native `measureInWindow` can report a transformed width while the
    // press-scale is mid-animation, which re-wraps the clone narrower; the
    // onLayout size is always the rendered truth, so use it for the clone size
    // and reserve measureInWindow for position only.
    const bubbleSizeRef = useRef({ width: 0, height: 0 });
    const onBubbleLayout = useCallback((e: LayoutChangeEvent) => {
      bubbleSizeRef.current = {
        width: e.nativeEvent.layout.width,
        height: e.nativeEvent.layout.height,
      };
    }, []);
    // The text wraps at the column's maxWidth = BUBBLE_COLUMN_FRACTION of the
    // row's content box. The column shrinks to the bubble (so it can't be
    // measured directly) — derive the wrap width from the measured ROW + the
    // shared geometry constants, so a style tweak updates layout AND this
    // together. (The bubble's own padding/font never drift: the SAME bubble
    // element is shared into the clone.)
    const rowWidthRef = useRef(0);
    const onRowLayout = useCallback((e: LayoutChangeEvent) => {
      rowWidthRef.current = e.nativeEvent.layout.width;
    }, []);
    // The bubble shrinks slightly while held (press feedback); the lift then
    // restores it from this scale as the menu opens.
    const [pressed, setPressed] = useState(false);

    // Measure the bubble's on-screen rect and present the menu over it.
    // On web, RN's `measureInWindow` returns the PRE-FLIP layout position
    // inside the inverted (`scaleY(-1)`) list — a bottom bubble reports a
    // near-top y. The DOM's `getBoundingClientRect` is transform-aware (true
    // visual viewport coords), so use it on web; native's measureInWindow
    // already accounts for the flip.
    const presentMenu = useCallback(() => {
      setPressed(false);
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch(() => null);
      const node = bubbleRef.current;
      if (!node) return;
      // Wrap width = column maxWidth = a fraction of the row's content box (row
      // width minus its horizontal padding). Uses the SAME constants as the
      // styles, so it stays in sync with them.
      const rowW = rowWidthRef.current;
      const maxWidth =
        rowW > 0
          ? (rowW - 2 * rpx(ROW_PADDING_RPX)) * BUBBLE_COLUMN_FRACTION
          : bubbleSizeRef.current.width;
      const present = (rect: BubbleMenuTarget['rect']) =>
        onPresentMenu?.({
          message,
          isMine,
          rect,
          maxWidth: maxWidth || rect.width,
          clone: cloneRef.current,
        });
      if (Platform.OS === 'web') {
        const el = node as unknown as HTMLElement;
        const r = el.getBoundingClientRect();
        present({ x: r.left, y: r.top, width: r.width, height: r.height });
      } else {
        // Size from onLayout (rendered truth); measureInWindow only for x/y.
        const { width, height } = bubbleSizeRef.current;
        node.measureInWindow((x, y, mw, mh) =>
          present({ x, y, width: width || mw, height: height || mh }),
        );
      }
    }, [onPresentMenu, message, isMine, rpx]);

    const menuable = isMenuable(message, !!onPresentMenu);

    // A single long-press gesture on the shared bubble container — works for
    // ANY payload (text/media/audio/file/cards) without each renderer wiring
    // it: a quick tap still reaches the renderer's own touchable (the gesture
    // only activates after the hold), a hold presents the menu. Press-down
    // scale is native-only (a web mouse-down shouldn't shrink the bubble; web
    // also opens via right-click below).
    const longPress = useMemo(
      () =>
        Gesture.LongPress()
          .enabled(menuable)
          .minDuration(300)
          .onBegin(() => {
            if (Platform.OS !== 'web') runOnJS(setPressed)(true);
          })
          .onStart(() => runOnJS(presentMenu)())
          .onFinalize(() => {
            if (Platform.OS !== 'web') runOnJS(setPressed)(false);
          }),
      [menuable, presentMenu],
    );

    // Web extra: also trigger via right-click, suppressing the browser's native
    // context menu. (Right-click sends no touch hold, so it never double-fires
    // with the long-press above.) Bound on the bubble's DOM node — RN-web View
    // ref === the element.
    useEffect(() => {
      if (Platform.OS !== 'web') return;
      if (!isMenuable(message, !!onPresentMenu)) return;
      const node = bubbleRef.current as unknown as HTMLElement | null;
      if (!node) return;
      const onContextMenu = (e: Event) => {
        e.preventDefault();
        presentMenu();
      };
      node.addEventListener('contextmenu', onContextMenu);
      return () => node.removeEventListener('contextmenu', onContextMenu);
    }, [message, onPresentMenu, presentMenu]);
    const handleQuotePress = useCallback(
      () => onQuotePress?.(message),
      [onQuotePress, message],
    );
    const handleRetry = useCallback(
      () => onRetry?.(message),
      [onRetry, message],
    );
    const handleShowReaders = useCallback(
      () => onShowReaders?.(message),
      [onShowReaders, message],
    );
    const handleAudioPlay = useCallback(
      () => onAudioPlay?.(message),
      [onAudioPlay, message],
    );
    const handleReactToggle = useCallback(
      (emoji: string) => onReact?.(message, emoji),
      [onReact, message],
    );

    // The payload is the parsed domain object (the DTO boundary already
    // parsed it) — read it directly and narrow on its discriminant.
    const { payload } = message;

    // Decide whether to render a time-slice marker before this row.
    const showTimeMarker = useMemo(() => {
      if (!prevMessageCreatedAt) return true; // first message in list
      const cur = new Date(message.createdAt).getTime();
      const prev = new Date(prevMessageCreatedAt).getTime();
      return cur - prev > TIME_SLICE_GAP_MS;
    }, [message.createdAt, prevMessageCreatedAt]);

    // The marker must sit visually ABOVE its message. On a backend whose
    // inverted cell reverses child order (FlatList) that means rendering it
    // LAST; on one that doesn't (FlashList) it means rendering it FIRST —
    // `timeMarkerFirst` carries that backend decision.
    const timeMarker = showTimeMarker ? (
      <MessageTimeMarker
        createdAt={message.createdAt}
        style={{ marginVertical: rpx(8) }}
      />
    ) : null;
    const leadingMarker = timeMarkerFirst ? timeMarker : null;
    const trailingMarker = timeMarkerFirst ? null : timeMarker;

    // System messages (room.dismissed, members.invited, …) render as
    // centered grey text — not a bubble. Bypass the normal flow.
    if (message.authorType === 'system' && payload.type === 'notification') {
      return (
        <>
          {leadingMarker}
          <SystemMessageText
            payload={payload}
            myPlatformUserId={myPlatformUserId}
            style={{ marginVertical: rpx(8) }}
          />
          {trailingMarker}
        </>
      );
    }

    // Edits/deletes only apply to confirmed (remote) messages.
    const deletedAt = message.kind === 'remote' ? message.deletedAt : null;
    const editedAt = message.kind === 'remote' ? message.editedAt : null;

    let displayText = '[消息]';
    if (deletedAt) {
      displayText = '[已撤回]';
    } else if (payload.type === 'text') {
      displayText = payload.text;
    } else if (payload.type === 'markdown') {
      displayText = payload.markdown;
    }

    // Inline `@mention` highlight ranges, only for a live text payload.
    const textMentions =
      !deletedAt && payload.type === 'text' ? payload.mentions : undefined;

    const quote = message.quoteSnapshot;
    // Rich payloads (image/video/audio/file/cards) render via
    // MessagePayloadView WITHOUT the colored text bubble. Deleted messages
    // always fall back to the "[已撤回]" text path.
    const richType = !deletedAt && isRichPayloadType(payload.type);
    // Author name above bubble (WeChat-style) only for received bubbles in
    // group rooms. Private rooms already display the counterpart in the
    // conversation header; repeating it on every bubble is noise.
    const showAuthorNameAbove =
      !isMine && roomType === 'group' && !!message.authorName;

    // Own-message status column. A local message shows its send status; a
    // confirmed own message shows the read-check once others have read it.
    const statusKind: OwnMessageStatusKind =
      message.kind === 'local'
        ? message.status
        : isMine && !message.deletedAt && (message.readByCount ?? 0) > 0
        ? 'read'
        : null;

    // The author name (group received, WeChat-style) above the bubble — inline
    // only, not part of the lifted clone.
    const authorNameEl = showAuthorNameAbove ? (
      <Typography.Text
        size={rpx(11)}
        color="#9ca3af"
        style={{
          fontFamily: FontFamily.NotoSans.Medium,
          marginBottom: rpx(4),
          marginLeft: rpx(2),
        }}
      >
        {message.authorName}
      </Typography.Text>
    ) : null;

    // The bubble itself (payload only). This exact element is BOTH measured and
    // lifted as the clone, so the clone is the bubble — sized to its content,
    // wrapping identically.
    const bubble = (
      <>
        {richType ? (
          <View style={{ alignItems: isMine ? 'flex-end' : 'flex-start' }}>
            {quote ? (
              <BubbleQuoteSnapshot
                quote={quote}
                tone="standalone"
                isMine={isMine}
                onPress={handleQuotePress}
                style={{ marginBottom: rpx(6) }}
              />
            ) : null}
            <MessagePayloadView
              payload={payload}
              isMine={isMine}
              onPreviewImage={onPreviewImage}
              onPreviewVideo={onPreviewVideo}
              onOpenUrl={onOpenUrl}
              onOpenCard={onOpenCard}
              audioUnread={
                !isMine && message.kind === 'remote' && !message.audioPlayedAt
              }
              onAudioPlay={handleAudioPlay}
              isBot={isBotMessage}
              onSuggestionPress={onSuggestionPress}
            />
          </View>
        ) : (
          <View
            style={{
              paddingHorizontal: rpx(14),
              paddingVertical: rpx(10),
              borderRadius: rpx(16),
              ...(isMine
                ? {
                    borderTopRightRadius: rpx(4),
                    backgroundColor: '#3b82f6',
                  }
                : {
                    borderTopLeftRadius: rpx(4),
                    backgroundColor: RECEIVED_BUBBLE_BG,
                  }),
            }}
          >
            {quote ? (
              <BubbleQuoteSnapshot
                quote={quote}
                tone="bubble"
                isMine={isMine}
                onPress={handleQuotePress}
                style={{ marginBottom: rpx(6) }}
              />
            ) : null}
            <MentionedText
              text={displayText}
              mentions={textMentions}
              size={rpx(14)}
              lineHeight={rpx(20)}
              isMine={isMine}
            />
            {editedAt && !deletedAt ? (
              <Typography.Text
                size={rpx(10)}
                color={isMine ? 'rgba(255,255,255,0.7)' : '#9ca3af'}
                style={{ alignSelf: 'flex-end', marginTop: rpx(2) }}
              >
                已编辑
              </Typography.Text>
            ) : null}
          </View>
        )}
      </>
    );
    // Avatar + own-status siblings — inline row only (not lifted). The bot
    // gets the default 服务助手 glyph when it has no custom avatar.
    const avatarEl = !isMine ? (
      isBotMessage && !message.authorAvatar ? (
        <View style={{ marginRight: rpx(8), marginTop: rpx(2) }}>
          <BotAvatar size={rpx(28)} radius={rpx(14)} />
        </View>
      ) : (
        <Avatar
          uri={message.authorAvatar ?? undefined}
          name={message.authorName ?? '?'}
          textStyle={{
            fontFamily: FontFamily.NotoSans.Medium,
            fontSize: rpx(12),
            lineHeight: rpx(16),
          }}
          style={{
            width: rpx(28),
            height: rpx(28),
            borderRadius: rpx(14),
            marginRight: rpx(8),
            marginTop: rpx(2),
          }}
        />
      )
    ) : null;
    // Read receipt / send status — anchored to the bubble's bottom-LEFT
    // (absolute, so it neither shrinks the bubble's wrap width nor drops onto
    // the reactions row hanging below the bubble).
    const statusEl = statusKind ? (
      <View
        style={{
          position: 'absolute',
          right: '100%',
          bottom: rpx(2),
          marginRight: rpx(6),
        }}
      >
        <OwnMessageStatus
          kind={statusKind}
          onRetry={handleRetry}
          onShowReaders={handleShowReaders}
        />
      </View>
    ) : null;

    // The lift is JUST the bubble (no avatar/status), measured precisely.
    cloneRef.current = bubble;

    // Pixel cap measured once at the list level (FlashList's non-deterministic
    // cell sizing drops a `%` cap, so the bubble would stretch full-width);
    // `%` fallback until the list width is known.
    const columnMaxWidth = maxBubbleWidth ?? COLUMN_MAX_WIDTH;

    return (
      <>
        {leadingMarker}
        <View
          onLayout={onRowLayout}
          style={[
            {
              flexDirection: 'row',
              justifyContent: isMine ? 'flex-end' : 'flex-start',
              backgroundColor: highlighted
                ? 'rgba(59, 130, 246, 0.12)'
                : 'transparent',
              paddingVertical: rpx(4),
              paddingHorizontal: rpx(ROW_PADDING_RPX),
              borderRadius: rpx(8),
            },
            style,
          ]}
        >
          {avatarEl}
          <View style={{ maxWidth: columnMaxWidth }}>
            {authorNameEl}
            {/* `bubbleRef` wraps ONLY the bubble and sizes to its content
                (alignSelf), so the long-press measure is the bubble's exact
                rect. The press-scale is a transform inside, so it doesn't
                change that measured box. The status hangs off its bottom-left
                via absolute positioning (out of flow → measure unaffected). */}
            <View
              ref={bubbleRef}
              onLayout={onBubbleLayout}
              style={{
                position: 'relative',
                alignSelf: isMine ? 'flex-end' : 'flex-start',
              }}
            >
              <GestureDetector gesture={longPress}>
                <MotiView
                  animate={{ scale: pressed ? BUBBLE_PRESS_SCALE : 1 }}
                  transition={{ type: 'timing', duration: 320 }}
                >
                  {bubble}
                </MotiView>
              </GestureDetector>
              {statusEl}
            </View>
            {message.kind === 'remote' && message.reactions?.length ? (
              <BubbleReactions
                reactions={message.reactions}
                isMine={isMine}
                onToggle={handleReactToggle}
              />
            ) : null}
          </View>
        </View>
        {trailingMarker}
      </>
    );
  },
);
